import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Input, input, Output, signal } from '@angular/core';

/**
 * The `FlatTabSelectorComponent` is a standalone Angular component that provides a flat tab selector UI.
 * It allows users to switch between different tabs and emits an event when the selected tab index changes.
 *
 * @example
 * ```html
 * <flat-tab-selector
 *   [tabs]="['Tab 1', 'Tab 2', 'Tab 3']"
 *   [currentIndex]="0"
 *   (indexChange)="onTabIndexChange($event)">
 * </flat-tab-selector>
 * ```
 *
 * @property {string[]} tabs - An array of tabs to be displayed in the flat tab selector component.
 * @property {EventEmitter<number>} indexChange - Event emitter for index changes.
 * @property {number} currentIndex - The index to set as the current selected index.
 */
@Component({
  selector: 'flat-tab-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flat-tab-selector.component.html',
  styleUrl: './flat-tab-selector.component.scss',
})
export class FlatTabSelectorComponent {
  /**
   * An array of tabs to be displayed in the flat tab selector component.
   * This property is initialized with an empty array.
   */
  public readonly tabs = input([]);
  @Output() public readonly indexChange = new EventEmitter<number>(); // Event emitter for index changes
  /**
   * A signal representing the currently selected tab index.
   * Initialized to 0 by default.
   */
  protected readonly selectedIndex = signal(0);

  /**
   * Computes the position of the selector as a percentage string.
   *
   * @returns {string} The position of the selector in percentage format.
   */
  protected readonly getSelectorPosition = computed(() => {
    return `${(this.selectedIndex() / this.tabs().length) * 100}%`;
  });

  /**
   * Computes the width of each tab as a percentage string based on the number of tabs.
   *
   * @returns {string} The width of each tab in percentage format (e.g., "25%").
   */
  protected readonly getTabWidth = computed(() => `${100 / this.tabs().length}%`);

  /**
   * Sets the current index of the tab selector.
   *
   * @param index - The index to set as the current selected index.
   */
  @Input() set currentIndex(index: number) {
    this.selectedIndex.set(index);
  }

  /**
   * Selects a tab based on the provided index.
   *
   * @param index - The index of the tab to select.
   *
   * Updates the selectedIndex signal and emits the indexChange event.
   */
  protected selectTab(index: number) {
    this.selectedIndex.set(index); // Update the signal
    this.indexChange.emit(index); // Emit the event
  }
}
